/* eslint-disable react/prop-types */

import {
  AddIcon,
  ChevronDownIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ChevronUpIcon,
  DeleteIcon,
  EditIcon,
  SearchIcon,
} from "@chakra-ui/icons";
import {
  Badge,
  Box,
  Button,
  Collapse,
  HStack,
  IconButton,
  Input,
  InputGroup,
  InputLeftElement,
  Link,
  Text,
  Tooltip,
  VStack,
} from "@chakra-ui/react";
import { useEffect, useState } from "react";
import { GoGrabber } from "react-icons/go";

import { DndContext, DragOverlay, pointerWithin } from "@dnd-kit/core";
import { restrictToWindowEdges, snapCenterToCursor } from "@dnd-kit/modifiers";
import {
  SortableContext,
  arrayMove,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

/* =======================
   Sortable Section
======================= */

const SortableSection = ({ id, isDraggingItem, children }) => {
  const { setNodeRef, transform, transition, attributes, listeners } =
    useSortable({ id, data: { type: "section" } });

  return (
    <Box
      ref={setNodeRef}
      style={{
        // Suppress section movement entirely while an item is being dragged —
        // otherwise the whole section (including its children) jumps around.
        transform: isDraggingItem
          ? undefined
          : CSS.Transform.toString(transform),
        transition: isDraggingItem ? undefined : transition,
      }}
    >
      {children({ attributes, listeners })}
    </Box>
  );
};

/* =======================
   Sortable Item
======================= */

const SortableItem = ({ id, children }) => {
  const {
    setNodeRef,
    transform,
    transition,
    attributes,
    listeners,
    isDragging,
  } = useSortable({ id, data: { type: "item" } });

  return (
    <Box
      ref={setNodeRef}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.4 : 1,
      }}
    >
      {children({ attributes, listeners })}
    </Box>
  );
};

/* =======================
   Main Component
======================= */

const AdminSearchableSidebar = ({
  sections,
  onSectionClick,
  onItemClick,
  onAddSection,
  onEditSection,
  onDeleteSection,
  onReorderSections,
  activeSectionId,
  activeItemId,
  isOpen,
  onToggle,
}) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [expandedSectionId, setExpandedSectionId] = useState(null);
  const [draggingItem, setDraggingItem] = useState(null);
  const [draggingSection, setDraggingSection] = useState(null);
  const [isDraggingItem, setIsDraggingItem] = useState(false);
  const [hoveredSectionId, setHoveredSectionId] = useState(null);

  useEffect(() => {
    if (activeSectionId) {
      setExpandedSectionId(activeSectionId);
    }
  }, [activeSectionId]);

  const filteredSections = sections
    .map((section) => ({
      ...section,
      items: section.items.filter((item) =>
        item.title.toLowerCase().includes(searchQuery.toLowerCase()),
      ),
    }))
    .filter((section) => section.items.length >= 0);

  const toggleSection = (sectionId) => {
    setExpandedSectionId((prev) => (prev === sectionId ? null : sectionId));
  };

  // ─── Helpers ───────────────────────────────────────────────────────────────

  const findSectionOfItem = (itemId) =>
    sections.find((s) => s.items.some((i) => i.id === itemId));

  const findItem = (itemId) => {
    for (const section of sections) {
      const item = section.items.find((i) => i.id === itemId);
      if (item) return item;
    }
    return null;
  };

  const isSection = (id) => sections.some((s) => s.id === id);
  const isItem = (id) => sections.some((s) => s.items.some((i) => i.id === id));

  // ─── Drag start ────────────────────────────────────────────────────────────

  const handleDragStart = ({ active }) => {
    if (isSection(active.id)) {
      setDraggingSection(sections.find((s) => s.id === active.id));
      setIsDraggingItem(false);
    } else if (isItem(active.id)) {
      setDraggingItem(findItem(active.id));
      setIsDraggingItem(true);
    }
  };

  // ─── Drag over (highlight target section) ─────────────────────────────────

  const handleDragOver = ({ active, over }) => {
    if (!over || !isItem(active.id)) {
      setHoveredSectionId(null);
      return;
    }
    // Resolve which section the cursor is over
    const overSection =
      findSectionOfItem(over.id) ?? sections.find((s) => s.id === over.id);

    const sourceSection = findSectionOfItem(active.id);

    // Only highlight if it's a different section
    if (overSection && overSection.id !== sourceSection?.id) {
      setHoveredSectionId(overSection.id);
    } else {
      setHoveredSectionId(null);
    }
  };

  // ─── Drag end ──────────────────────────────────────────────────────────────

  const handleDragEnd = ({ active, over }) => {
    setDraggingItem(null);
    setDraggingSection(null);
    setIsDraggingItem(false);
    setHoveredSectionId(null);

    if (!over || active.id === over.id) return;

    // ── Section reorder ──
    if (isSection(active.id) && isSection(over.id)) {
      const oldIndex = sections.findIndex((s) => s.id === active.id);
      const newIndex = sections.findIndex((s) => s.id === over.id);
      onReorderSections?.(arrayMove(sections, oldIndex, newIndex));
      return;
    }

    // ── Item move (same or cross-section) ──
    if (isItem(active.id)) {
      const sourceSection = findSectionOfItem(active.id);
      if (!sourceSection) return;

      // `over` can be another item or a section header
      const targetSection =
        findSectionOfItem(over.id) ?? sections.find((s) => s.id === over.id);

      if (!targetSection) return;

      const isSameSection = sourceSection.id === targetSection.id;

      const updatedSections = sections.map((section) => {
        // Same section — reorder in place
        if (isSameSection && section.id === sourceSection.id) {
          const oldIndex = section.items.findIndex((i) => i.id === active.id);
          const newIndex = section.items.findIndex((i) => i.id === over.id);

          if (newIndex === -1) {
            // Dropped on the section header → append
            const movedItem = section.items[oldIndex];
            const rest = section.items.filter((i) => i.id !== active.id);
            return { ...section, items: [...rest, movedItem] };
          }

          return {
            ...section,
            items: arrayMove(section.items, oldIndex, newIndex),
          };
        }

        // Cross-section — remove from source
        if (!isSameSection && section.id === sourceSection.id) {
          return {
            ...section,
            items: section.items.filter((i) => i.id !== active.id),
          };
        }

        // Cross-section — insert into target
        if (!isSameSection && section.id === targetSection.id) {
          const movedItem = findItem(active.id);
          const overIndex = section.items.findIndex((i) => i.id === over.id);

          if (overIndex === -1) {
            // Dropped on the section header → append
            return { ...section, items: [...section.items, movedItem] };
          }

          const newItems = [...section.items];
          newItems.splice(overIndex, 0, movedItem);
          return { ...section, items: newItems };
        }

        return section;
      });

      onReorderSections?.(updatedSections);

      // Keep target section open so the user sees where the item landed
      if (!isSameSection) {
        setExpandedSectionId(targetSection.id);
      }
    }
  };

  // ─── Colour map ────────────────────────────────────────────────────────────

  const typeColors = {
    powerbi: "purple",
    tableau: "orange",
    directory: "blue",
    file: "gray",
    static: "teal",
  };

  // Single flat list: [sectionId, ...itemIds, sectionId, ...itemIds, ...]
  const allIds = sections.flatMap((s) => [s.id, ...s.items.map((i) => i.id)]);

  return (
    <Box
      bg="white"
      borderLeft="1px"
      borderColor="gray.200"
      transition="all 0.3s"
      w={isOpen ? "400px" : "50px"}
      display="flex"
      flexDirection="column"
    >
      {/* Toggle */}
      <Box p={2} borderBottom="1px" borderColor="gray.200">
        <IconButton
          icon={isOpen ? <ChevronRightIcon /> : <ChevronLeftIcon />}
          onClick={onToggle}
          size="sm"
          aria-label="Toggle sidebar"
        />
      </Box>

      <Collapse in={isOpen} animateOpacity style={{ flex: 1 }}>
        <Box display="flex" flexDirection="column" height="100%">
          {/* Search */}
          <Box p={4} borderBottom="1px" borderColor="gray.100">
            <InputGroup size="sm" mb={3}>
              <InputLeftElement pointerEvents="none">
                <SearchIcon color="gray.400" />
              </InputLeftElement>
              <Input
                placeholder="Search topics..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </InputGroup>

            <Button
              leftIcon={<AddIcon />}
              size="sm"
              colorScheme="teal"
              w="100%"
              onClick={onAddSection}
            >
              Add Directory
            </Button>
          </Box>

          {/* Single DndContext for everything */}
          <Box flex={1} overflowY="auto" px={4} py={4}>
            <DndContext
              collisionDetection={pointerWithin}
              modifiers={[snapCenterToCursor, restrictToWindowEdges]}
              onDragStart={handleDragStart}
              onDragOver={handleDragOver}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={allIds}
                strategy={verticalListSortingStrategy}
              >
                <VStack align="stretch" spacing={1}>
                  {filteredSections.map((section) => (
                    <SortableSection
                      key={section.id}
                      id={section.id}
                      isDraggingItem={isDraggingItem}
                    >
                      {({ attributes, listeners }) => (
                        <Box>
                          {/* Section Header */}
                          <Box
                            display="flex"
                            alignItems="center"
                            justifyContent="space-between"
                            py={2}
                            px={3}
                            borderRadius="md"
                            bg={
                              hoveredSectionId === section.id
                                ? "blue.50"
                                : activeSectionId === section.id
                                  ? "teal.50"
                                  : "gray.50"
                            }
                            border="2px solid"
                            borderColor={
                              hoveredSectionId === section.id
                                ? "blue.300"
                                : "transparent"
                            }
                            transition="all 0.15s ease"
                            cursor="pointer"
                            onClick={() => {
                              onSectionClick(section);
                              setExpandedSectionId(section.id);
                            }}
                          >
                            {/* Section drag handle */}
                            <Box
                              {...attributes}
                              {...listeners}
                              cursor="grab"
                              pr={2}
                              color="gray.500"
                              _hover={{ color: "gray.700" }}
                              onClick={(e) => e.stopPropagation()}
                            >
                              <GoGrabber />
                            </Box>

                            <Text
                              fontWeight="bold"
                              fontSize="sm"
                              flex={1}
                              noOfLines={1}
                            >
                              {section.title}
                            </Text>

                            <HStack spacing={1}>
                              <Tooltip label="Edit Section">
                                <IconButton
                                  border="none"
                                  icon={<EditIcon />}
                                  size="xs"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    onEditSection(section);
                                  }}
                                />
                              </Tooltip>

                              <Tooltip label="Delete Section">
                                <IconButton
                                  icon={<DeleteIcon color="red" />}
                                  border="none"
                                  size="xs"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    onDeleteSection(section);
                                  }}
                                />
                              </Tooltip>

                              <IconButton
                                icon={
                                  expandedSectionId === section.id ? (
                                    <ChevronUpIcon />
                                  ) : (
                                    <ChevronDownIcon />
                                  )
                                }
                                size="xs"
                                border="none"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  toggleSection(section.id);
                                }}
                              />
                            </HStack>
                          </Box>

                          {/* Items */}
                          <Collapse in={expandedSectionId === section.id}>
                            <VStack align="stretch" spacing={0} mt={1} ml={4}>
                              {section.items.map((item) => (
                                <SortableItem key={item.id} id={item.id}>
                                  {({
                                    attributes: iAttr,
                                    listeners: iList,
                                  }) => (
                                    <Link
                                      onClick={() => onItemClick(item)}
                                      px={2}
                                      py={1.5}
                                      borderRadius="md"
                                      display="block"
                                      bg={
                                        activeItemId === item.id
                                          ? "blue.50"
                                          : "transparent"
                                      }
                                      _hover={{
                                        bg: "gray.50",
                                        textDecoration: "none",
                                      }}
                                    >
                                      <HStack gap={1} spacing={1}>
                                        {/* Item drag handle */}
                                        <Box
                                          {...iAttr}
                                          {...iList}
                                          cursor="grab"
                                          color="gray.800"
                                          _hover={{ color: "gray.500" }}
                                          flexShrink={0}
                                          onClick={(e) => e.stopPropagation()}
                                        >
                                          <GoGrabber size={13} />
                                        </Box>

                                        <Text fontSize="sm" noOfLines={1}>
                                          {item.title}
                                        </Text>

                                        <Badge
                                          fontSize="2xs"
                                          colorScheme={
                                            typeColors[item.type] || "gray"
                                          }
                                        >
                                          {item.type === "directory"
                                            ? "Folder"
                                            : item.type}
                                        </Badge>
                                      </HStack>
                                    </Link>
                                  )}
                                </SortableItem>
                              ))}
                            </VStack>
                          </Collapse>
                        </Box>
                      )}
                    </SortableSection>
                  ))}
                </VStack>
              </SortableContext>

              {/* Floating ghost while dragging */}
              <DragOverlay dropAnimation={null}>
                {draggingSection ? (
                  <Box
                    display="flex"
                    alignItems="center"
                    py={2}
                    px={3}
                    borderRadius="md"
                    bg="teal.50"
                    boxShadow="lg"
                    border="1px solid"
                    borderColor="teal.200"
                    opacity={0.9}
                    w="260px"
                  >
                    <GoGrabber style={{ marginRight: 8, color: "gray" }} />
                    <Text fontWeight="bold" fontSize="sm" noOfLines={1}>
                      {draggingSection.title}
                    </Text>
                  </Box>
                ) : draggingItem ? (
                  <Box
                    px={3}
                    py={1.5}
                    borderRadius="md"
                    bg="white"
                    boxShadow="lg"
                    border="1px solid"
                    borderColor="blue.300"
                    opacity={0.9}
                    w="260px"
                  >
                    <HStack gap={1}>
                      <GoGrabber size={13} color="gray" />
                      <Text fontSize="sm" noOfLines={1} flex={1}>
                        {draggingItem.title}
                      </Text>
                      <Badge
                        fontSize="2xs"
                        colorScheme={typeColors[draggingItem.type] || "gray"}
                      >
                        {draggingItem.type === "directory"
                          ? "Folder"
                          : draggingItem.type}
                      </Badge>
                    </HStack>
                  </Box>
                ) : null}
              </DragOverlay>
            </DndContext>
          </Box>
        </Box>
      </Collapse>
    </Box>
  );
};

export default AdminSearchableSidebar;
